// @flow @format
import Client from 'lib/client';
import {encodeEmail} from './util';

export type User = {
	name: string,
	login: string,
};

export type Machine = {
	bandwith: number,
	id: number,
	last_backup: string,
	last_changes: string,
	last_connected: string,
	mid: string,
	name: string,
	online: boolean,
	space_active: number,
	space_disk: number,
	space_selected: number,
	version: string,
};

export type Repository = {
	active_space: number,
	disk_space: number,
	total_space: number,
	deleted: boolean,
	id: string,
	last_backup: string,
	last_changes: string,
	last_connected: string,
	mid: string,
	name: string,
	owner: string,
	root: string,
};

export type File = {
	created: string,
	deleted: false,
	id: string,
	name: string,
	num_versions: number,
	repo: string,
	size: number,
	type: 0 | 1,
};

export type FileNode = {
	breadcrumb: {id: string, name: string}[],
	files: File[],
	repository: {id: string, name: string},
	machine: {id: string, name: string},
};

export type Metafile = {
	Created: string,
	Dirty: 0 | 1,
	Hash: string,
	Id: string,
	Name: string,
	Parent: string,
	Repository: string,
	Size: number,
	Type: 0 | 1,
	VersionFin: number,
	VersionIni: number,
};

export type Session = {
	admin: 1 | 0,
	config: string,
	group: number,
	name: string,
	resp: number,
	token: string,
	user: string,
	server: string,
};

export class PublicApi {
	static LOGIN_URL = 'https://login.cloner.cl';

	_loginClient: Client;

	constructor() {
		this._loginClient = new Client(
			PublicApi.LOGIN_URL,
			new Headers({
				Referer: 'https://login.cloner.cl',
			})
		);
	}

	login(user: string, password: string): Promise<Session> {
		const body = JSON.stringify({user: user, pass: password});

		return this._loginClient
			.get(`/api/server?login=${encodeEmail(user)}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(`cloner-api: invalid user or password`);
			})
			.then(data => {
				const server = data.slice(0, data.length - 1);
				const client = new Client(
					server,
					new Headers({Referer: server})
				);

				return client
					.post('/api/user/login', 'application/json', body)
					.then(resp => {
						if (resp.ok) {
							return resp
								.json()
								.then(json => ({...json, server}));
						}
						return Promise.reject(
							`cloner-api: invalid user or password`
						);
					});
			});
	}
}

export class PrivateApi {
	_client: Client;

	constructor(server: string, token: string) {
		this._client = new Client(
			server,
			new Headers({
				cloner_key: token,
				Accept: 'application/json, text/plain,/',
				Connection: 'keep-alive',
				Referer: 'https://login.cloner.cl',
				'Accept-Language': 'es-ES,es;q=0.8',
				'Accept-Encoding': 'gzip, deflate, sdch, br',
			})
		);
	}

	getUsersByGroup(id: number): Promise<User[]> {
		return this._client.get(`/api/all_users?group=${id}`).then(resp => {
			if (resp.ok) {
				return resp.json();
			}
			return Promise.reject(
				`cloner-api: could not fetch all users for group ${id}`
			);
		});
	}

	getMachinesByUser(email: string): Promise<Machine[]> {
		return this._client
			.get(`/api/machines?user=${encodeEmail(email)}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json().then(data => data.machines_info);
				}
				return Promise.reject(
					`cloner-api: could not fetch machines for user "${email}"`
				);
			});
	}

	getRepositoriesByMachine(id: string, user: string): Promise<Repository[]> {
		return this._client
			.get(`/api/repositories?id=${id}&user=${encodeURIComponent(user)}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json().then(data => data.repositories);
				}
				return Promise.reject(
					`cloner-api: could not fetch repositories for machine "${id}"`
				);
			});
	}

	getFiles(
		id: string,
		repoId: string,
		version: number = 99999999
	): Promise<FileNode> {
		return this._client
			.get(`/api/files?id=${id}&repoid=${repoId}&version=${version}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(
					`cloner-api: could not fetch children of file "${id}"`
				);
			});
	}

	getFileVersions(id: string, repoId: string): Promise<FileNode> {
		return this._client
			.get(`/api/fileshistory?id=${id}&repoid=${repoId}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(
					`cloner-api: could not fetch versions of file "${id}"`
				);
			});
	}

	getMetafile(id: string, repoId: string): Promise<Metafile> {
		return this._client
			.get(`/api/metafile?id=${id}&repo_id=${repoId}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(
					`cloner-api: could not fetch metafile for file "${id}"`
				);
			});
	}
}



// WEBPACK FOOTER //
// ./src/lib/cloner-api/api.js